import {
    Box,
    Button,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"
import SaveIcon from "@material-ui/icons/Save"
import ScheduleIcon from "@material-ui/icons/Schedule"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useState } from "react"
import { EstabelecimentoAberto } from "../../../services/clientefiel"
import type {
    DiaSemana,
    PeriodoEntrega,
    PeriodoEntregaPayload,
} from "../../../services/periodoEntregaFornecedor"
import {
    adicionarPeriodo,
    atualizarPeriodo,
    atualizarStatusDelivery,
    excluirPeriodo,
} from "../../../services/periodoEntregaFornecedor"
import useAppStore from "../../../stores/AppStore"
import useStatusEstabelecimentoStore from "../../../stores/StatusEstabelecimentoStore"
import { PeriodoEntregaFornecedorActionsTypes } from "../context/periodoEntregaFornecedorActions"
import { usePeriodoEntregaFornecedorContext } from "../context/usePeriodoEntregaFornecedorContext"

const DIAS_SEMANA: { valor: DiaSemana; label: string }[] = [
    { valor: "DOMINGO", label: "Domingo" },
    { valor: "SEGUNDA", label: "Segunda-feira" },
    { valor: "TERCA", label: "Terça-feira" },
    { valor: "QUARTA", label: "Quarta-feira" },
    { valor: "QUINTA", label: "Quinta-feira" },
    { valor: "SEXTA", label: "Sexta-feira" },
    { valor: "SABADO", label: "Sábado" },
]

const TIPOS_ENTREGA = [
    { valor: 0, label: "Apenas Delivery" },
    { valor: 1, label: "Apenas Retirar no local" },
    { valor: 2, label: "Delivery e Retirar no local" },
]

const STATUS_DELIVERY = [
    { valor: 0, label: "Fechado" },
    { valor: 1, label: "Em Operação" },
    { valor: 2, label: "Pausado" },
]

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderLeft: {
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    statusSelect: {
        minWidth: 160,
    },
    addButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        "&:hover": { backgroundColor: "#388e3c" },
        boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
    },
    deleteRowButton: {
        color: "#e74c3c",
    },
    novoTurnoRow: {
        backgroundColor: "#f0fff0",
    },
    statusChip: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 12,
        fontWeight: 600,
        fontSize: "0.85rem",
    },
}))

type LinhaEditavel = {
    diaSemana: DiaSemana | null
    horaInicioTxt: string
    horaFimTxt: string
    tipoEntrega: number
}

const linhaVazia = (): LinhaEditavel => ({
    diaSemana: null,
    horaInicioTxt: "",
    horaFimTxt: "",
    tipoEntrega: 2,
})

function statusColor(status: number) {
    if (status === 1) return "#4caf50"
    if (status === 2) return "#ff9800"
    return "#e74c3c"
}

export function PeriodoEntregaFornecedorComponent() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { enqueueSnackbar } = useSnackbar()
    const { dados, dispatch } = usePeriodoEntregaFornecedorContext()

    const [novaLinha, setNovaLinha] = useState<LinhaEditavel | null>(null)
    const [salvandoNova, setSalvandoNova] = useState(false)
    const [editando, setEditando] = useState<Record<number, LinhaEditavel>>({})
    const [salvandoId, setSalvandoId] = useState<number | null>(null)
    const [excluindoId, setExcluindoId] = useState<number | null>(null)
    const [salvandoStatus, setSalvandoStatus] = useState(false)
    const [statusLocal, setStatusLocal] = useState<number>(dados.statusDelivery)

    React.useEffect(() => {
        setStatusLocal(dados.statusDelivery)
    }, [dados.statusDelivery])

    const configs = useAppStore(s => s.configuracoes)
    const estabelecimentoId = configs?.user?.parametros?.estabelecimento?.id
    const solicitarAtualizacaoStatus = useStatusEstabelecimentoStore(s => s.solicitarAtualizacao)
    const [online, setOnline] = useState<boolean | null>(null)

    const atualizarStatusReal = React.useCallback(async () => {
        if (!estabelecimentoId) return
        try {
            const resp: any = await EstabelecimentoAberto(estabelecimentoId)
            if (resp && !resp.retornoErro && typeof resp.online === "boolean") {
                setOnline(resp.online)
            }
        } catch {}
    }, [estabelecimentoId])

    React.useEffect(() => {
        atualizarStatusReal()
    }, [atualizarStatusReal, dados.statusDelivery])

    const notificarMudancaStatus = React.useCallback(() => {
        atualizarStatusReal()
        solicitarAtualizacaoStatus()
    }, [atualizarStatusReal, solicitarAtualizacaoStatus])

    const foraDoHorario = dados.statusDelivery === 1 && online === false

    const handleSalvarNovo = async () => {
        if (!novaLinha) return
        const auth = localStorage.getItem("chaveSistema") ?? ""
        setSalvandoNova(true)
        try {
            const payload: PeriodoEntregaPayload = {
                diaSemana: novaLinha.diaSemana,
                horaInicioTxt: novaLinha.horaInicioTxt,
                horaFimTxt: novaLinha.horaFimTxt,
                tipoEntrega: novaLinha.tipoEntrega,
            }
            const resultado = await adicionarPeriodo(auth, payload)
            if ("retornoErro" in resultado) {
                enqueueSnackbar(resultado.mensagem, { variant: "error" })
            } else {
                dispatch({
                    type: PeriodoEntregaFornecedorActionsTypes.ADICIONAR_PERIODO,
                    payload: resultado,
                })
                setNovaLinha(null)
                notificarMudancaStatus()
                enqueueSnackbar("Turno adicionado com sucesso!", { variant: "success" })
            }
        } finally {
            setSalvandoNova(false)
        }
    }

    const handleSalvarEdicao = async (periodo: PeriodoEntrega) => {
        const linha = editando[periodo.id]
        if (!linha) return
        const auth = localStorage.getItem("chaveSistema") ?? ""
        setSalvandoId(periodo.id)
        try {
            const payload: PeriodoEntregaPayload = {
                diaSemana: linha.diaSemana,
                horaInicioTxt: linha.horaInicioTxt,
                horaFimTxt: linha.horaFimTxt,
                tipoEntrega: linha.tipoEntrega,
            }
            const resultado = await atualizarPeriodo(auth, periodo.id, payload)
            if ("retornoErro" in resultado) {
                enqueueSnackbar(resultado.mensagem, { variant: "error" })
            } else {
                dispatch({
                    type: PeriodoEntregaFornecedorActionsTypes.ATUALIZAR_PERIODO,
                    payload: resultado,
                })
                setEditando(prev => {
                    const next = { ...prev }
                    delete next[periodo.id]
                    return next
                })
                notificarMudancaStatus()
                enqueueSnackbar("Turno atualizado com sucesso!", { variant: "success" })
            }
        } finally {
            setSalvandoId(null)
        }
    }

    const handleExcluir = async (id: number) => {
        const auth = localStorage.getItem("chaveSistema") ?? ""
        setExcluindoId(id)
        try {
            const resultado = await excluirPeriodo(auth, id)
            if (resultado && "retornoErro" in resultado) {
                enqueueSnackbar(resultado.mensagem, { variant: "error" })
            } else {
                dispatch({
                    type: PeriodoEntregaFornecedorActionsTypes.REMOVER_PERIODO,
                    payload: id,
                })
                notificarMudancaStatus()
                enqueueSnackbar("Turno removido com sucesso!", { variant: "success" })
            }
        } finally {
            setExcluindoId(null)
        }
    }

    const handleSalvarStatus = async () => {
        const auth = localStorage.getItem("chaveSistema") ?? ""
        setSalvandoStatus(true)
        try {
            const resultado = await atualizarStatusDelivery(auth, statusLocal)
            if ("retornoErro" in resultado) {
                enqueueSnackbar(resultado.mensagem, { variant: "error" })
            } else {
                dispatch({
                    type: PeriodoEntregaFornecedorActionsTypes.ATUALIZAR_STATUS,
                    payload: resultado.statusDelivery,
                })
                notificarMudancaStatus()
                enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" })
            }
        } finally {
            setSalvandoStatus(false)
        }
    }

    const salvandoTurnos = salvandoNova || salvandoId !== null
    const temTurnosPendentes = novaLinha !== null || Object.keys(editando).length > 0

    const handleSalvarTurnos = async () => {
        const idsEmEdicao = Object.keys(editando).map(Number)
        for (const id of idsEmEdicao) {
            const periodo = dados.periodos.find(p => p.id === id)
            if (periodo) await handleSalvarEdicao(periodo)
        }
        if (novaLinha !== null) await handleSalvarNovo()
    }

    const getLinha = (periodo: PeriodoEntrega): LinhaEditavel =>
        editando[periodo.id] ?? {
            diaSemana: periodo.diaSemana,
            horaInicioTxt: periodo.horaInicioTxt,
            horaFimTxt: periodo.horaFimTxt,
            tipoEntrega: periodo.tipoEntrega,
        }

    const setLinha = (id: number, campo: keyof LinhaEditavel, valor: string | number | null) => {
        const periodo = dados.periodos.find(p => p.id === id)
        if (!periodo) return
        const atual = getLinha(periodo)
        setEditando(prev => ({
            ...prev,
            [id]: { ...atual, [campo]: valor },
        }))
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    className={classes.titleText}
                    gutterBottom
                >
                    Horários de Delivery
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                    Configure aqui os horários que seu delivery funciona.
                </Typography>

                {/* Card de status do delivery */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardHeaderLeft}>
                            <ScheduleIcon className={classes.iconHeader} />
                            <Typography variant="subtitle1" className={classes.cardHeaderTitle}>
                                Situação do Delivery
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.statusSelect}
                            >
                                <Select
                                    value={statusLocal}
                                    onChange={e => setStatusLocal(Number(e.target.value))}
                                    renderValue={value => {
                                        const val = Number(value)
                                        const exibeForaHorario = val === 1 && online === false
                                        const label = exibeForaHorario
                                            ? "Fora do horário"
                                            : STATUS_DELIVERY.find(s => s.valor === val)?.label
                                        const cor = exibeForaHorario ? "#fb8c00" : statusColor(val)
                                        return (
                                            <span
                                                className={classes.statusChip}
                                                style={{ color: "white", backgroundColor: cor }}
                                            >
                                                {label}
                                            </span>
                                        )
                                    }}
                                >
                                    {STATUS_DELIVERY.map(s => (
                                        <MenuItem key={s.valor} value={s.valor}>
                                            <span
                                                className={classes.statusChip}
                                                style={{
                                                    color: "white",
                                                    backgroundColor: statusColor(s.valor),
                                                }}
                                            >
                                                {s.label}
                                            </span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={salvandoStatus || statusLocal === dados.statusDelivery}
                                onClick={handleSalvarStatus}
                                style={{
                                    borderRadius: 25,
                                    backgroundColor:
                                        statusLocal !== dados.statusDelivery
                                            ? "#4caf50"
                                            : undefined,
                                    color:
                                        statusLocal !== dados.statusDelivery ? "white" : undefined,
                                }}
                            >
                                {salvandoStatus ? "Salvando..." : "Salvar status"}
                            </Button>
                        </Box>
                    </Box>
                    {foraDoHorario && (
                        <Box className={classes.cardContent} style={{ paddingTop: 0 }}>
                            <Typography variant="body2" style={{ color: "#e67e22" }}>
                                Sua loja está marcada como <strong>Em Operação</strong>, mas está{" "}
                                <strong>fora do horário de funcionamento</strong> configurado nos
                                turnos abaixo. Os clientes verão a loja como fechada até o próximo
                                turno.
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Card de turnos */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardHeaderLeft}>
                            <ScheduleIcon className={classes.iconHeader} />
                            <Typography variant="subtitle1" className={classes.cardHeaderTitle}>
                                Turnos de Entrega
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                className={classes.addButton}
                                disabled={novaLinha !== null}
                                onClick={() => setNovaLinha(linhaVazia())}
                            >
                                Novo Turno
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<SaveIcon />}
                                disabled={!temTurnosPendentes || salvandoTurnos}
                                onClick={handleSalvarTurnos}
                                style={{
                                    borderRadius: 25,
                                    backgroundColor:
                                        temTurnosPendentes && !salvandoTurnos
                                            ? "#4caf50"
                                            : undefined,
                                    color:
                                        temTurnosPendentes && !salvandoTurnos ? "white" : undefined,
                                }}
                            >
                                {salvandoTurnos ? "Salvando..." : "Salvar"}
                            </Button>
                        </Box>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ minWidth: 620, width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 150 }}>
                                            Dia da Semana
                                        </TableCell>
                                        <TableCell style={{ minWidth: 110 }}>Início</TableCell>
                                        <TableCell style={{ minWidth: 110 }}>Fim</TableCell>
                                        <TableCell style={{ minWidth: 170 }}>
                                            Tipo de Entrega
                                        </TableCell>
                                        <TableCell style={{ minWidth: 80 }} align="center">
                                            Ação
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.periodos.map(periodo => {
                                        const linha = getLinha(periodo)
                                        const excluindo = excluindoId === periodo.id
                                        return (
                                            <TableRow key={periodo.id}>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={linha.diaSemana ?? ""}
                                                            onChange={e =>
                                                                setLinha(
                                                                    periodo.id,
                                                                    "diaSemana",
                                                                    (e.target.value as DiaSemana) ||
                                                                        null
                                                                )
                                                            }
                                                            displayEmpty
                                                            variant="outlined"
                                                        >
                                                            <MenuItem value="">
                                                                <em>Selecione</em>
                                                            </MenuItem>
                                                            {DIAS_SEMANA.map(d => (
                                                                <MenuItem
                                                                    key={d.valor}
                                                                    value={d.valor}
                                                                >
                                                                    {d.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        placeholder="hh:mm"
                                                        value={linha.horaInicioTxt}
                                                        onChange={e =>
                                                            setLinha(
                                                                periodo.id,
                                                                "horaInicioTxt",
                                                                e.target.value
                                                            )
                                                        }
                                                        inputProps={{ maxLength: 5 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        placeholder="hh:mm"
                                                        value={linha.horaFimTxt}
                                                        onChange={e =>
                                                            setLinha(
                                                                periodo.id,
                                                                "horaFimTxt",
                                                                e.target.value
                                                            )
                                                        }
                                                        inputProps={{ maxLength: 5 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            value={linha.tipoEntrega}
                                                            onChange={e =>
                                                                setLinha(
                                                                    periodo.id,
                                                                    "tipoEntrega",
                                                                    Number(e.target.value)
                                                                )
                                                            }
                                                            variant="outlined"
                                                        >
                                                            {TIPOS_ENTREGA.map(t => (
                                                                <MenuItem
                                                                    key={t.valor}
                                                                    value={t.valor}
                                                                >
                                                                    {t.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        className={classes.deleteRowButton}
                                                        disabled={excluindo}
                                                        onClick={() => handleExcluir(periodo.id)}
                                                        title="Excluir"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}

                                    {/* Linha de novo turno */}
                                    {novaLinha !== null && (
                                        <TableRow className={classes.novoTurnoRow}>
                                            <TableCell>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={novaLinha.diaSemana ?? ""}
                                                        onChange={e =>
                                                            setNovaLinha({
                                                                ...novaLinha,
                                                                diaSemana:
                                                                    (e.target.value as DiaSemana) ||
                                                                    null,
                                                            })
                                                        }
                                                        displayEmpty
                                                        variant="outlined"
                                                    >
                                                        <MenuItem value="">
                                                            <em>Selecione</em>
                                                        </MenuItem>
                                                        {DIAS_SEMANA.map(d => (
                                                            <MenuItem key={d.valor} value={d.valor}>
                                                                {d.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="hh:mm"
                                                    value={novaLinha.horaInicioTxt}
                                                    onChange={e =>
                                                        setNovaLinha({
                                                            ...novaLinha,
                                                            horaInicioTxt: e.target.value,
                                                        })
                                                    }
                                                    inputProps={{ maxLength: 5 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    placeholder="hh:mm"
                                                    value={novaLinha.horaFimTxt}
                                                    onChange={e =>
                                                        setNovaLinha({
                                                            ...novaLinha,
                                                            horaFimTxt: e.target.value,
                                                        })
                                                    }
                                                    inputProps={{ maxLength: 5 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={novaLinha.tipoEntrega}
                                                        onChange={e =>
                                                            setNovaLinha({
                                                                ...novaLinha,
                                                                tipoEntrega: Number(e.target.value),
                                                            })
                                                        }
                                                        variant="outlined"
                                                    >
                                                        {TIPOS_ENTREGA.map(t => (
                                                            <MenuItem key={t.valor} value={t.valor}>
                                                                {t.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    className={classes.deleteRowButton}
                                                    onClick={() => setNovaLinha(null)}
                                                    title="Cancelar"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {dados.periodos.length === 0 && novaLinha === null && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                    Nenhum turno cadastrado. Clique em "Novo Turno"
                                                    para adicionar.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
